<template>
  <div v-if="moduleDetails && initialLoadFinished">
    <div v-if="moduleDetails.programme_id" class="sidebar-title">
      <router-link :class="{'icon-only': sidebarOpen} " :to="{ name: 'programme_curriculum', params: { programmeId: this.moduleDetails.programme_id }}" title="Back to programme page">
        <i class="fas fa-angle-left" aria-hidden="true"></i>
        <small>Back to programme</small>
      </router-link>
    </div>
    <ol class="sidebar-panel-nav">
      <li class="nav-group nav-group-title module-overview">
        <router-link
          :to="{ name: 'module_landing', params: { moduleId: this.moduleDetails.id } }"
          :class="{ 'nav-item--current': isModuleOverviewActive }">
          <i class="fas fa-home" aria-hidden="true"></i><span>Module Overview</span>
        </router-link>
        <k-progress v-if="chapterAssetCompletionScores.length" class="squared module-progress"
          shape="bar"
          :percentage="moduleCompletionPercentage"
          :precision="1"
          :show-bar-text="true"
          :valueMax="chapterAssetCompletionScores.length"
          customText="Completed"
          ariaLabel="Overall Module Progress"
          size="small"
        ></k-progress>
      </li>
      <chapter v-for="chapter in chapters"
        :key="chapter.name"
        :moduleName="moduleDetails.name"
        :chapter="chapter">
      </chapter>
      <li v-if="knowledgeBaseEnabled" class="nav-group nav-group-title knowledge-base">
        <router-link
          :to="{ name: 'module_knowledge_base' }"
          :class="{ 'nav-item--current': isKnowledgeBaseActive }">
          <i class="k-asset-icon fa-duotone fa-comments fa-swap-opacity" aria-hidden="true"></i><span>Knowledge Base</span>
          <span v-if="kbQuestionCount > 0" class="count-bubble" :aria-label="`${pluralize('thread', kbQuestionCount, true)}`">{{ kbQuestionCount }}</span>
        </router-link>
      </li>
      <li v-if="$featureFlags.isEnabled('keymaker-app-page') && keyMakerEnabled" class="nav-group nav-group-title learner-keymaker-portal">
        <router-link
          :to="{ name: 'learner_keymaker_portal' }"
          :class="{ 'nav-item--current': isLearnerKeymakerPortalActive }">
          <i class="fas fa-key" aria-hidden="true"></i><span>API Keys</span>
        </router-link>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.module-overview {
  display: flex;
  flex-direction: column;
}

.module-progress {
  width: 100%;
}
</style>

<script>
import useAppProgrammeStore from '@stores/app-programme-store';
import SidebarMixin from '@mixins/sidebar-mixin';
import KProgress from '@base-components/k-progress.vue';
import PluralMixin from '@mixins/plural-mixin';
import { getCurrentSidebarSection } from '@utils/current-asset-from-route';
import Chapter from './chapter.vue';

const SIDEBAR_EXPAND_TRANSITION_MS = 200;

function isVisibleInScrollContainer(element, container) {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  if (elementRect.height === 0 || elementRect.width === 0) {
    return false;
  }
  return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
}

export default {
  components: {
    chapter: Chapter,
    KProgress,
  },

  mixins: [SidebarMixin, PluralMixin],

  props: {
    sidebarOpen: {
      type: Boolean,
    },
  },

  data() {
    return {
      programmeStore: useAppProgrammeStore(),
      moduleDetailsReady: false,
      threadsReady: false,
      initialLoadFinished: false,
    };
  },

  beforeMount() {
    this.$sidebar.setVisibility(true);
    this.$sidebar.ready = false;
    this.$sidebar.setModuleId(this.moduleId);
    this.addCallbacks();
    this.loadModuleData();
  },

  watch: {
    allReady() {
      if (this.allReady) {
        if (!this.initialLoadFinished) {
          this.initialLoadFinished = true;
        }
        this.$sidebar.ready = true;
        this.scrollToActiveNavItem();
      } else {
        this.$sidebar.ready = false;
      }
    },

    route() {
      if (this.knowledgeBaseEnabled) {
        this.$logger.info('Route changed, refreshing KB question count');
        this.getKbQuestionCount();
      }
      this.scrollToActiveNavItem();
    },

    moduleId() {
      this.$sidebar.setModuleId(this.moduleId);
    },

    moduleDetailsReady() {
      if (this.moduleDetailsReady && this.moduleDetails && this.knowledgeBaseEnabled) {
        this.getKbQuestionCount();
      }
    },

    sidebarOpen(isOpen) {
      if (isOpen) {
        this.scrollToActiveNavItem({ reason: 'expand' });
      }
    },
  },

  computed: {
    programme() {
      return this.programmeStore.getProgramme(Number(this.moduleDetails.programme_id));
    },

    keyMakerEnabled() {
      return Boolean(this.programme && this.programme.key_maker_enabled);
    },

    route() {
      return this.$route;
    },

    moduleId() {
      return Number(this.$route.params.moduleId);
    },

    knowledgeBaseEnabled() {
      return Boolean(this.moduleDetails && this.moduleDetails.knowledge_base_enabled);
    },

    apiEndpoint() {
      if (this.moduleId) {
        return `/api/curriculum/modules/${this.moduleId}`;
      }
      return undefined;
    },

    allReady() {
      if (this.knowledgeBaseEnabled) {
        return this.moduleDetailsReady && this.chaptersReady && this.threadsReady;
      }
      return this.moduleDetailsReady && this.chaptersReady;
    },

    isModuleOverviewActive() {
      return getCurrentSidebarSection(this.$route) === 'module_overview';
    },

    isKnowledgeBaseActive() {
      return getCurrentSidebarSection(this.$route) === 'knowledge_base';
    },

    isLearnerKeymakerPortalActive() {
      return getCurrentSidebarSection(this.$route) === 'learner_keymaker_portal';
    },
  },

  methods: {
    getModuleDetails() {
      this.$logger.info('Getting module details', { moduleId: this.moduleId }, true);
      this.moduleDetailsReady = false;
      this.$http.get(this.apiEndpoint)
        .then(response => {
          this.$logger.info('Got module details', { moduleId: this.moduleId });
          this.$sidebar.moduleDetails = response.data;
          this.programmeStore.currentProgrammeId = Number(response.data.programme_id);
        }).catch(err => {
          this.programmeStore.currentProgrammeId = null;
          if (err.response) {
            if (this.$http.errIn(err, [404])) {
              this.$logger.warn('Module not found', { moduleId: this.moduleId });
              this.$router.push({ name: '404', params: { pathMatch: '' } });
            } else if (this.$http.errIn(err, [403])) {
              this.$logger.warn('Access to restricted content', { moduleId: this.moduleId });
              this.$router.push({ name: '404', params: { pathMatch: '' } });
            } else if (this.$http.isWarning(err)) {
              this.$logger.warn('Error getting module details for sidebar', { moduleId: this.moduleId }, err);
              this.$ktoast.showError(err, true);
            } else {
              this.$logger.error('Error getting module details for sidebar', { moduleId: this.moduleId }, err);
              this.$ktoast.showError(err, true);
            }
          }
        }).then(() => {
          this.moduleDetailsReady = true;
        });
    },

    getChapters() {
      this.$logger.info('Getting module chapters', { moduleId: this.moduleId }, true);
      this.chaptersReady = false;
      this.$http.get(`${this.apiEndpoint}/chapters`)
        .then(response => {
          this.$sidebar.chapters = response.data.chapters;
          this.$logger.info('Got module chapters', { moduleId: this.moduleId });
        }).catch(err => {
          if (!this.$http.isWarning(err)) {
            this.$logger.error('Error getting module chapters', { moduleId: this.moduleId }, err);
            this.$ktoast.showError(err, true);
          } else {
            this.$logger.warn('Module not found', { moduleId: this.moduleId });
          }
        }).then(() => {
          this.chaptersReady = true;
        });
    },

    getKbQuestionCount() {
      if (!(this.moduleDetailsReady && this.knowledgeBaseEnabled)) {
        return;
      }
      this.$logger.info('Getting module KB question count', { moduleId: this.moduleId }, true);
      this.threadsReady = false;
      this.$http.get(`/api/curriculum/knowledge/${this.moduleId}/threads/count`).then(result => {
        this.$sidebar.kbQuestionCount = result.data.total ?? 0;
        this.$logger.info('Got module KB question count', { moduleId: this.moduleId });
      }).catch(err => {
        if (err.response) {
          if (this.$http.isWarning(err)) {
            this.$logger.warn('KB question count not found', { moduleId: this.moduleId });
          } else {
            this.$logger.error('Error getting KB question count', { moduleId: this.moduleId }, err);
            this.$ktoast.showError(err, true);
          }
        }
      }).then(() => {
        this.threadsReady = true;
      });
    },

    loadModuleData() {
      this.$logger.info('Loading module sidebar data', { moduleId: this.moduleId }, true);
      this.$Loading.start();
      if (this.moduleId) {
        this.getModuleDetails();
        this.getKbQuestionCount();
        this.getChapters();
      }
    },

    addCallbacks() {
      this.$sidebar.setLoadModuleDataCallback(this.loadModuleData);
      this.$sidebar.setRefreshKbCountCallback(this.getKbQuestionCount);
    },

    async scrollToActiveNavItem({ reason } = {}) {
      if (!this.$sidebar.ready || !this.initialLoadFinished || !this.sidebarOpen) {
        return;
      }

      await this.$nextTick();

      if (reason === 'expand') {
        await new Promise(resolve => {
          setTimeout(resolve, SIDEBAR_EXPAND_TRANSITION_MS);
        });
      }

      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      const sidebarPanel = document.querySelector('aside.sidebar-panel');
      if (!sidebarPanel || sidebarPanel.classList.contains('collapsed')) {
        return;
      }

      const activeItem = sidebarPanel.querySelector('.nav-item--current');
      if (!activeItem) {
        return;
      }

      if (isVisibleInScrollContainer(activeItem, sidebarPanel)) {
        return;
      }

      const behavior = reason === 'expand' ? 'instant' : 'smooth';
      activeItem.scrollIntoView({ block: 'center', behavior });
    },
  },
};
</script>
